// src/components/extensions/ExtensionGrid.tsx
//
// Renders every extension from siteConfig.extensions, one per row. Data-driven
// end to end: adding an extension to the config adds a panel, and nothing here
// or in the card names an extension.
//
// The projects grid hoists a `featured` entry to the top and gives it a wider
// slot. Extensions have no such concept, so this list runs in config order and
// every panel is the same width — one less branch to keep in step.
import { useEffect, useRef, useState } from 'react';
import { useReducedMotion, useScroll, useTransform } from 'framer-motion';

import { siteConfig } from '../../config/siteConfig';
import { motionSet, STAGGER } from '../about/motion';
import ExtensionCard from './ExtensionCard';
import { GridItem, GridRoot, ProgressFill, ProgressTrack } from './ExtensionGrid.styles';

const viewport = { once: true, amount: 0.3 } as const;

/**
 * Where in the viewport the list is considered "started" and "finished".
 *
 * A single value for both ends, which is what makes the bar reach full only once
 * the last card has actually been read rather than when the list merely stops
 * entering the screen.
 */
const PROGRESS_BAND = 0.65;

/** Where the list sits, in document coordinates. */
interface ListMetrics {
  /** Offset of the list's top edge from the top of the document. */
  top: number;
  height: number;
  /** Window height, needed to turn the band into a pixel offset. */
  viewport: number;
}

const clamp = (value: number) => Math.min(1, Math.max(0, value));

const ExtensionGrid = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const anim = motionSet(reduceMotion);
  const listRef = useRef<HTMLUListElement>(null);

  const { extensions } = siteConfig;
  const total = extensions.length;

  const [metrics, setMetrics] = useState<ListMetrics | null>(null);

  /**
   * The window's scroll position, with no target.
   *
   * Deliberately not `useScroll({ target: listRef })`, which is the obvious way
   * to do this and logs a warning on every dev load. useScroll's `target`
   * measures the target's offset by walking `offsetParent` up to the scroll
   * container, and in dev it insists the container be position: relative /
   * absolute / fixed. With no container given, that container is
   * `document.scrollingElement` — the <html> element, which is `static` in every
   * document on earth. So the diagnostic cannot be satisfied without setting
   * `position: relative` on <html>, which is not a change worth making to
   * silence a message about a measurement that is already correct.
   *
   * Measuring the list here instead keeps the same behaviour: window scroll in,
   * progress out, no scroll listener, no re-render per frame.
   */
  const { scrollY } = useScroll();

  useEffect(() => {
    const node = listRef.current;
    if (!node) return undefined;

    const measure = () => {
      const rect = node.getBoundingClientRect();
      setMetrics({
        top: rect.top + window.scrollY,
        height: rect.height,
        viewport: window.innerHeight,
      });
    };

    measure();

    /**
     * Re-measured when the list changes size, not just when the window does.
     * The list grows late — a lazy demo image and webfonts both land after first
     * paint — and a height captured once at mount would leave the bar reaching
     * full at the wrong scroll position and never quite arriving.
     */
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(node);

    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  /**
   * Progress through the list, computed in scroll space.
   *
   * Reproduces the `['start 65%', 'end 65%']` offset pair: the bar is empty when
   * the list's top reaches the band, and full when its bottom does. Runs on
   * every scroll frame but writes to a MotionValue, so it does not re-render.
   */
  const railScale = useTransform(scrollY, (offset) => {
    if (!metrics) return 0;

    const band = PROGRESS_BAND * metrics.viewport;
    const start = metrics.top - band;
    const end = metrics.top + metrics.height - band;
    const span = end - start;

    // A list shorter than the band is over before it begins; reading it as
    // complete is the honest answer, and the fill is hidden under reduced
    // motion anyway.
    if (span <= 0) return 1;

    return clamp((offset - start) / span);
  });

  return (
    <>
      <ProgressTrack aria-hidden="true">
        <ProgressFill style={{ scaleX: railScale }} />
      </ProgressTrack>

      <GridRoot
        ref={listRef}
        variants={anim.group(STAGGER.list)}
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
      >
        {extensions.map((extension, index) => (
          <GridItem key={extension.id} variants={anim.group(STAGGER.list)}>
            <ExtensionCard
              extension={extension}
              index={index}
              total={total}
            />
          </GridItem>
        ))}
      </GridRoot>
    </>
  );
};

export default ExtensionGrid;
